import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Every container currently trapping focus, app-wide — lets one trap's
// focusin listener recognize "focus just landed inside a DIFFERENT active
// trap" (a nested/sibling modal, e.g. ReportModal opened on top of Comments,
// or a promptDialog opened on top of a still-open popover) and leave it
// alone, instead of yanking focus back to itself. Without this, whichever
// trap activated first "wins" and the newer modal on top never actually
// gets to hold focus at all — it opens visually but Tab/typing never
// reaches it.
const activeContainers = new Set();

// Keeps Tab/Shift+Tab cycling inside `containerRef`'s subtree while `active`,
// and restores focus to whatever was focused before it activated once it
// deactivates — every portaled dialog in the app locks background *scroll*
// but none constrained *focus*, so Tab could walk a keyboard user straight
// out into the page behind a modal that's still open.
export function useFocusTrap(containerRef, active, options) {
  const previouslyFocusedRef = useRef(null);
  const getInitialFocus = options?.getInitialFocus;

  useEffect(() => {
    if (!active) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    previouslyFocusedRef.current = document.activeElement;
    activeContainers.add(container);

    function focusables() {
      return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        el => el.offsetParent !== null
      );
    }

    // Something inside already has focus — an input/button's own `autoFocus`,
    // which React applies during commit, before this effect runs — so that
    // deliberate choice (e.g. DialogHost defaulting focus to Cancel on a
    // dangerous confirm) is respected instead of being stolen back to
    // whatever happens to be first in DOM order.
    if (!container.contains(document.activeElement)) {
      // A caller can point initial focus at something more meaningful than
      // "first in DOM order" — e.g. PickerSelect's already-selected option,
      // which the trap would otherwise skip past while the list's own
      // scroll-to-selected effect centers it off in a direction focus never
      // followed.
      const preferred = typeof getInitialFocus === "function" ? getInitialFocus() : null;
      const first = focusables()[0];
      (preferred || first || container).focus({ preventScroll: true });
    }

    function onKeyDown(e) {
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    container.addEventListener("keydown", onKeyDown);

    // Tab presses that happen while focus is INSIDE a cross-origin iframe
    // (Turnstile's captcha widget) never reach the keydown listener above —
    // an iframe is a separate browsing context, so its keystrokes don't
    // bubble into this document at all. The browser still fires `focusin`
    // on whatever element in THIS document ends up focused once Tab carries
    // focus out the far side of the iframe, though — catching that here and
    // redirecting back in is the only way to close that escape hatch.
    function onFocusIn(e) {
      if (container.contains(e.target)) return;
      // Focus landed somewhere outside this container — but if it's inside
      // ANOTHER currently-active trap (a modal opened on top of this one),
      // that's the correct, intended place for it right now; only reclaim
      // focus that escaped somewhere no trap owns at all.
      for (const other of activeContainers) {
        if (other !== container && other.contains(e.target)) return;
      }
      const items = focusables();
      (items[0] || container).focus({ preventScroll: true });
    }
    document.addEventListener("focusin", onFocusIn);

    return () => {
      activeContainers.delete(container);
      container.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      const toRestore = previouslyFocusedRef.current;
      if (toRestore && typeof toRestore.focus === "function" && document.contains(toRestore)) {
        toRestore.focus({ preventScroll: true });
      }
    };
    // getInitialFocus deliberately excluded — re-running this on every
    // identity change of an inline function prop would re-steal focus away
    // from wherever the user has since tabbed to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, containerRef]);
}
